import type { HistoricalData } from '@/queries/historical.query'
import { formatDate, formatUSD } from '@vaultsfyi/common/utils'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Loader } from '../common/loader'
import { ChartBrush, SingleAreaBrushPanorama } from './chartBrush'
import { ChartContainer } from './chartContainer'
import { ChartTooltip } from './chartTooltip'
import { ChartWatermark } from './chartWatermark'

interface AreaChartProps {
  data: HistoricalData
  className?: string
  showBrush?: boolean
}

export const TvlAreaChart = ({ data, className, showBrush = false }: AreaChartProps) => {
  if (data.length === 0) {
    return (
      <ChartContainer className={className}>
        <Loader />
      </ChartContainer>
    )
  }

  return (
    <ChartContainer className={className}>
      <ResponsiveContainer>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="tvlFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--chart-blue))" stopOpacity={1} />
              <stop offset="100%" stopColor="rgb(var(--chart-blue))" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="timestamp"
            tickLine={false}
            tickMargin={12}
            type="number"
            interval={'preserveStartEnd'}
            domain={['dataMin', 'dataMax']}
            tickCount={7}
            tickFormatter={(timestamp) => formatDate({ timestamp })}
          />
          <YAxis
            tickMargin={0}
            tickLine={false}
            tickFormatter={(value) => formatUSD(value, { minimumFractionDigits: 0 })}
            interval="preserveStartEnd"
          />
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgb(var(--border))" />
          <Tooltip content={({ payload }) => <TvlChartTooltip data={payload?.[0]?.payload} />} />
          <Area
            dataKey="tvl"
            type="monotone"
            isAnimationActive={false}
            stroke="rgb(var(--chart-blue))"
            strokeOpacity={1}
            strokeWidth={2}
            fill="url(#tvlFill)"
          />
          <ChartWatermark />
          {showBrush && (
            <ChartBrush data={data}>
              <SingleAreaBrushPanorama color="rgb(var(--chart-blue))" dataKey="tvl" gradientId="tvlBrushFill" />
            </ChartBrush>
          )}
        </AreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

interface TvlChartTooltip {
  data: HistoricalData[number]
}

const TvlChartTooltip = ({ data }: TvlChartTooltip) => {
  if (!data) {
    return null
  }
  return (
    <ChartTooltip>
      <span>{formatDate({ timestamp: data.timestamp, showYear: true })}</span>
      <p className="font-semibold">TVL: {formatUSD(data.tvl)}</p>
    </ChartTooltip>
  )
}
